/* ================================================================
   MORENO'S INTERACTIONS — v3.0 "FLAME"
   Track A: Storefront-only. Clean, conversion-focused.
   Flame/Ink brand. No heavy WebGL, no cursor glow, no particles.
   Respects prefers-reduced-motion. No dependencies.
   ================================================================ */

(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var TOUCH = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  var RAF = requestAnimationFrame;

  // Brand colors (matches morenos-brand-tokens.css)
  var FLAME = '#ff5a2c';
  var FLAME_HOT = '#ff3820';
  var INK_900 = '#0e0a08';
  var INK_800 = '#1a1310';
  var INK_700 = '#2a1e18';

  // ════════════════════════════════════════════════════════════════
  // 1. PAGE LOAD — clean ink fade-in
  // ════════════════════════════════════════════════════════════════
  function initPageLoad() {
    if (REDUCED) return;
    var overlay = document.createElement('div');
    overlay.setAttribute('aria-hidden', 'true');
    overlay.style.cssText =
      'position:fixed;inset:0;z-index:99998;background:' + INK_900 + ';' +
      'pointer-events:none;transition:opacity 0.6s cubic-bezier(0.22,1,0.36,1);';
    document.body.appendChild(overlay);
    setTimeout(function () {
      overlay.style.opacity = '0';
      setTimeout(function () { overlay.remove(); }, 700);
    }, 80);
  }

  // ════════════════════════════════════════════════════════════════
  // 2. SCROLL PROGRESS BAR — Flame gradient, 3px
  // ════════════════════════════════════════════════════════════════
  function initScrollProgress() {
    var bar = document.createElement('div');
    bar.id = 'morenos-scroll-progress';
    bar.setAttribute('aria-hidden', 'true');
    bar.style.cssText =
      'position:fixed;top:0;left:0;height:3px;z-index:9999;' +
      'background:linear-gradient(90deg,' + FLAME + ',' + FLAME_HOT + ');' +
      'width:0%;pointer-events:none;';
    document.body.appendChild(bar);

    window.addEventListener('scroll', function () {
      var pct = window.scrollY / (document.documentElement.scrollHeight - window.innerHeight) * 100;
      bar.style.width = Math.min(pct, 100) + '%';
    }, { passive: true });
  }

  // ════════════════════════════════════════════════════════════════
  // 3. SCROLL REVEAL — opacity + translate, multi-direction
  // ════════════════════════════════════════════════════════════════
  function initScrollReveal() {
    if (REDUCED) return;

    var DIRS = ['m-rv--up', 'm-rv--left', 'm-rv--right', 'm-rv--scale'];

    var SELS = [
      '.card', '.product-card-wrapper', '.collection-list__item',
      '.banner__content', '.rich-text__wrapper',
      '.image-with-text__grid', '.multicolumn-list__item',
      '.newsletter__wrapper', '.footer__content-top > *',
    ].join(',');

    function tag() {
      document.querySelectorAll(SELS).forEach(function (el, i) {
        if (el.classList.contains('m-rv')) return;
        el.classList.add('m-rv', DIRS[i % DIRS.length]);
        var row = Math.floor(i / 4);
        var col = i % 4;
        el.style.transitionDelay = (row * 50 + col * 25) + 'ms';
      });
    }

    var obs = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.add('m-in'); }
      });
    }, { threshold: 0.1, rootMargin: '80px 0px 40px 0px' });

    function observe() {
      document.querySelectorAll('.m-rv:not(.m-in)').forEach(function (el) { obs.observe(el); });
    }

    tag(); observe();
    document.addEventListener('shopify:section:load', function () { tag(); observe(); });
  }

  // ════════════════════════════════════════════════════════════════
  // 4. HERO PARALLAX — images move slower than scroll (desktop only)
  // ════════════════════════════════════════════════════════════════
  function initParallax() {
    if (REDUCED || TOUCH) return;

    var heroes = [];
    function collect() {
      heroes = [];
      document.querySelectorAll('.banner__media img, .slideshow__media img').forEach(function (img) {
        img.style.willChange = 'transform';
        img.style.transition = 'none';
        heroes.push(img);
      });
    }

    function tick() {
      var scrollY = window.scrollY;
      heroes.forEach(function (img) {
        var rect = img.parentElement.getBoundingClientRect();
        if (rect.bottom > 0 && rect.top < window.innerHeight) {
          img.style.transform = 'translateY(' + (scrollY * 0.25) + 'px) scale(1.08)';
        }
      });
      RAF(tick);
    }

    collect();
    RAF(tick);
    document.addEventListener('shopify:section:load', collect);
  }

  // ════════════════════════════════════════════════════════════════
  // 5. RIPPLE ON BUTTONS — subtle white ripple
  // ════════════════════════════════════════════════════════════════
  function initRipple() {
    if (REDUCED) return;

    document.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('button,.button,.shopify-payment-button__button,[type="submit"]');
      if (!btn) return;
      btn.classList.add('m-rh');
      var r = btn.getBoundingClientRect();
      var sz = Math.max(r.width, r.height);
      var rp = document.createElement('span');
      rp.className = 'm-rp';
      rp.style.cssText = 'width:' + sz + 'px;height:' + sz + 'px;left:' +
        (e.clientX - r.left - sz / 2) + 'px;top:' + (e.clientY - r.top - sz / 2) + 'px';
      btn.appendChild(rp);
      setTimeout(function () { rp.remove(); }, 600);
    });
  }

  // ════════════════════════════════════════════════════════════════
  // 6. ANNOUNCEMENT MARQUEE — endless scroll
  // ════════════════════════════════════════════════════════════════
  function initMarquee() {
    if (REDUCED) return;

    var bar = document.querySelector('.announcement-bar__message');
    if (!bar) return;

    var text = bar.textContent.trim();
    if (!text || text.length < 5) return;

    // Only marquee if text is short enough (single-message bar)
    if (bar.querySelectorAll('p, span').length > 2) return;

    var separator = '  ·  ';
    var repeated = (text + separator).repeat(6);

    bar.style.cssText = 'overflow:hidden;white-space:nowrap;width:100%';
    bar.innerHTML = '<span class="m-marquee" style="display:inline-block;' +
      'animation:m-scroll 24s linear infinite">' + repeated + '</span>';

    var css = document.createElement('style');
    css.textContent = '@keyframes m-scroll{0%{transform:translateX(0)}100%{transform:translateX(-50%)}}';
    document.head.appendChild(css);
  }

  // ════════════════════════════════════════════════════════════════
  // 7. KEN BURNS — slow zoom on card/product images
  // ════════════════════════════════════════════════════════════════
  function initKenBurns() {
    if (REDUCED) return;

    var css = document.createElement('style');
    css.textContent = [
      '.m-kb{overflow:hidden}',
      '.m-kb img{transition:transform 8s cubic-bezier(.25,.46,.45,.94)!important}',
      '.m-kb.m-kb-active img{transform:scale(1.06)}',
    ].join('\n');
    document.head.appendChild(css);

    var obs = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        e.target.classList.toggle('m-kb-active', e.isIntersecting);
      });
    }, { threshold: 0.2 });

    document.querySelectorAll('.card__media, .banner__media').forEach(function (el) {
      el.classList.add('m-kb');
      obs.observe(el);
    });
  }

  // ════════════════════════════════════════════════════════════════
  // 8. URGENCY BADGES — flame pulse on sale/new badges
  // ════════════════════════════════════════════════════════════════
  function initUrgencyBadges() {
    document.querySelectorAll('.badge').forEach(function (b) {
      var t = b.textContent.toLowerCase();
      if (t.includes('sale') || t.includes('rabatt') || t.includes('angebot') || t.includes('neu')) {
        b.classList.add('m-ub');
      }
    });
  }

  // ════════════════════════════════════════════════════════════════
  // 9. BACK TO TOP — Flame hover
  // ════════════════════════════════════════════════════════════════
  function initBackToTop() {
    var btn = document.createElement('button');
    btn.id = 'morenos-back-to-top';
    btn.setAttribute('aria-label', 'Back to top');
    btn.setAttribute('type', 'button');
    btn.innerHTML = '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">' +
      '<path d="M10 15V5M10 5l-4 4M10 5l4 4" stroke="currentColor" stroke-width="2" ' +
      'stroke-linecap="round" stroke-linejoin="round"/></svg>';
    btn.style.cssText =
      'position:fixed;bottom:24px;right:24px;z-index:999;width:48px;height:48px;' +
      'display:flex;align-items:center;justify-content:center;' +
      'background:' + INK_700 + ';border:1px solid rgba(253,251,247,0.12);border-radius:50%;' +
      'cursor:pointer;color:#fdfbf7;opacity:0;visibility:hidden;' +
      'transition:opacity .25s ease,visibility .25s ease,background .15s ease,transform .15s ease;';

    btn.onmouseenter = function () {
      btn.style.background = FLAME;
      btn.style.borderColor = FLAME;
      btn.style.transform = 'translateY(-2px)';
    };
    btn.onmouseleave = function () {
      btn.style.background = INK_700;
      btn.style.borderColor = 'rgba(253,251,247,0.12)';
      btn.style.transform = '';
    };
    btn.onclick = function () { window.scrollTo({ top: 0, behavior: REDUCED ? 'auto' : 'smooth' }); };
    document.body.appendChild(btn);

    var vis = false;
    window.addEventListener('scroll', function () {
      var show = window.scrollY > 400;
      if (show !== vis) {
        vis = show;
        btn.style.opacity = show ? '1' : '0';
        btn.style.visibility = show ? 'visible' : 'hidden';
      }
    }, { passive: true });
  }

  // ════════════════════════════════════════════════════════════════
  // 10. SMOOTH ANCHOR SCROLL
  // ════════════════════════════════════════════════════════════════
  function initSmoothAnchors() {
    document.addEventListener('click', function (e) {
      var a = e.target.closest && e.target.closest('a[href^="#"]');
      if (!a) return;
      var id = a.getAttribute('href').slice(1);
      if (!id) return;
      var target = document.getElementById(id);
      if (!target) return;
      e.preventDefault();
      target.scrollIntoView({ behavior: REDUCED ? 'auto' : 'smooth' });
    });
  }

  // ════════════════════════════════════════════════════════════════
  // 11. HORIZONTAL SLIDE — product grid items
  // ════════════════════════════════════════════════════════════════
  function initHorizontalSlide() {
    if (REDUCED) return;

    var obs = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.add('m-hs-in'); }
      });
    }, { threshold: 0.1, rootMargin: '80px 0px 40px 0px' });

    document.querySelectorAll('.grid__item, .collection-product-card').forEach(function (el, i) {
      el.classList.add('m-hslide', i % 2 === 0 ? 'm-hs-even' : 'm-hs-odd');
      el.style.transitionDelay = (i % 4) * 80 + 'ms';
      obs.observe(el);
    });
  }

  // ════════════════════════════════════════════════════════════════
  // INIT
  // ════════════════════════════════════════════════════════════════
  function init() {
    initPageLoad();
    initScrollProgress();
    initScrollReveal();
    initParallax();
    initRipple();
    initMarquee();
    initKenBurns();
    initUrgencyBadges();
    initBackToTop();
    initSmoothAnchors();
    initHorizontalSlide();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
